import { createContext, useCallback, useContext, useMemo, useState, useSyncExternalStore, type ReactNode } from 'react';
import { Maximize2, MicOff, PhoneCall, VideoOff } from 'lucide-react';
import { useDailyCall, type UseDailyCallReturn } from '@/hooks/useDailyCall';
import { DailyVideoTrack } from '@/components/video/DailyVideoTrack';
import { cn } from '@/lib/utils';

type NavigateToPath = (path: string) => void | Promise<void>;

const NAVIGATION_EVENT = 'mediflow:navigation';

let historyPatched = false;

interface VideoCallProviderProps {
  children: ReactNode;
  navigateToPath?: NavigateToPath;
}

interface VideoCallSessionOptions {
  skipPreJoin?: boolean;
}

interface VideoCallContextValue {
  dailyCall: UseDailyCallReturn;
  activeSessionId: string | null;
  skipPreJoin: boolean;
  isMinimized: boolean;
  hasLiveCall: boolean;
  beginSession: (sessionId: string, options?: VideoCallSessionOptions) => void;
  activateSession: (sessionId: string, options?: VideoCallSessionOptions) => void;
  registerWorkspaceSession: (sessionId: string, options?: VideoCallSessionOptions) => void;
  minimizeSession: (sessionId?: string) => void;
  restoreSession: () => void;
  clearSession: (sessionId?: string) => void;
  clearActiveSession: (sessionId?: string) => void;
}

const VideoCallContext = createContext<VideoCallContextValue | null>(null);

function patchHistoryNavigationEvents() {
  if (typeof window === 'undefined' || historyPatched) {
    return;
  }

  historyPatched = true;

  const originalPushState = window.history.pushState;
  const originalReplaceState = window.history.replaceState;

  window.history.pushState = function pushState(...args) {
    const result = originalPushState.apply(this, args);
    window.dispatchEvent(new Event(NAVIGATION_EVENT));
    return result;
  };

  window.history.replaceState = function replaceState(...args) {
    const result = originalReplaceState.apply(this, args);
    window.dispatchEvent(new Event(NAVIGATION_EVENT));
    return result;
  };
}

function subscribeToNavigation(onStoreChange: () => void) {
  if (typeof window === 'undefined') {
    return () => undefined;
  }

  patchHistoryNavigationEvents();

  window.addEventListener('popstate', onStoreChange);
  window.addEventListener(NAVIGATION_EVENT, onStoreChange);

  return () => {
    window.removeEventListener('popstate', onStoreChange);
    window.removeEventListener(NAVIGATION_EVENT, onStoreChange);
  };
}

function getNavigationSnapshot() {
  if (typeof window === 'undefined') {
    return '/';
  }

  return `${window.location.pathname}${window.location.search}`;
}

function buildCallRoute(sessionId: string, skipPreJoin: boolean): string {
  return skipPreJoin
    ? `/app/video-consultation?sessionId=${sessionId}&skipPreJoin=1`
    : `/app/video-consultation?sessionId=${sessionId}`;
}

function defaultNavigateToPath(path: string): void {
  if (typeof window === 'undefined') {
    return;
  }

  window.history.pushState({}, '', path);
  window.dispatchEvent(new Event(NAVIGATION_EVENT));
}

function isActiveSessionVisible(locationKey: string, sessionId: string | null) {
  if (!sessionId) {
    return false;
  }

  const url = new URL(locationKey, 'https://mediflow.local');

  return url.pathname === '/app/video-consultation'
    && url.searchParams.get('sessionId') === sessionId;
}

export function VideoCallProvider({ children, navigateToPath = defaultNavigateToPath }: VideoCallProviderProps) {
  const dailyCall = useDailyCall();
  const [activeSessionId, setActiveSessionId] = useState<string | null>(null);
  const [skipPreJoin, setSkipPreJoin] = useState(false);
  const locationKey = useSyncExternalStore(subscribeToNavigation, getNavigationSnapshot, () => '/');

  const hasLiveCall = activeSessionId !== null
    && (dailyCall.callState === 'connecting'
      || dailyCall.callState === 'connected'
      || dailyCall.callState === 'disconnecting');
  const isMinimized = hasLiveCall && !isActiveSessionVisible(locationKey, activeSessionId);

  const beginSession = useCallback((sessionId: string, options?: VideoCallSessionOptions) => {
    setActiveSessionId(sessionId);
    setSkipPreJoin(Boolean(options?.skipPreJoin));
  }, []);

  const registerWorkspaceSession = useCallback((sessionId: string, options?: VideoCallSessionOptions) => {
    if (activeSessionId !== sessionId) {
      return;
    }

    setSkipPreJoin(Boolean(options?.skipPreJoin));
  }, [activeSessionId]);

  const minimizeSession = useCallback((sessionId?: string) => {
    if (!hasLiveCall) {
      return;
    }

    if (sessionId && activeSessionId !== sessionId) {
      return;
    }

    void navigateToPath('/app/video-consultation');
  }, [activeSessionId, hasLiveCall, navigateToPath]);

  const restoreSession = useCallback(() => {
    if (!activeSessionId) {
      return;
    }

    void navigateToPath(buildCallRoute(activeSessionId, skipPreJoin));
  }, [activeSessionId, navigateToPath, skipPreJoin]);

  const clearSession = useCallback((sessionId?: string) => {
    if (sessionId && activeSessionId !== sessionId) {
      return;
    }

    setActiveSessionId(null);
    setSkipPreJoin(false);
  }, [activeSessionId]);

  const value = useMemo<VideoCallContextValue>(() => ({
    dailyCall,
    activeSessionId,
    skipPreJoin,
    isMinimized,
    hasLiveCall,
    beginSession,
    activateSession: beginSession,
    registerWorkspaceSession,
    minimizeSession,
    restoreSession,
    clearSession,
    clearActiveSession: clearSession,
  }), [
    activeSessionId,
    beginSession,
    clearSession,
    dailyCall,
    hasLiveCall,
    isMinimized,
    minimizeSession,
    registerWorkspaceSession,
    restoreSession,
    skipPreJoin,
  ]);

  return <VideoCallContext.Provider value={value}>{children}</VideoCallContext.Provider>;
}

export function useOptionalVideoCallContext() {
  return useContext(VideoCallContext);
}

export function useOptionalPracticeVideoCall() {
  return useOptionalVideoCallContext();
}

export function useVideoCallContext() {
  const context = useContext(VideoCallContext);

  if (!context) {
    throw new Error('useVideoCallContext muss innerhalb von VideoCallProvider verwendet werden.');
  }

  return context;
}

export function usePracticeVideoCall() {
  const context = useVideoCallContext();

  return {
    dailyCall: context.dailyCall,
    activeSessionId: context.activeSessionId,
    hasPersistentCall: context.hasLiveCall,
    isMinimized: context.isMinimized,
    activateSession: context.beginSession,
    minimizeSession: context.minimizeSession,
    clearActiveSession: context.clearSession,
    restoreCall: context.restoreSession,
  };
}

export function FloatingVideoCallWindow() {
  const context = useVideoCallContext();

  if (!context.activeSessionId || !context.hasLiveCall || !context.isMinimized) {
    return null;
  }

  const statusLabel = context.dailyCall.callState === 'connecting'
    ? 'Verbindung wird im Hintergrund aufgebaut.'
    : 'Gespräch bleibt aktiv, bis du es explizit beendest.';
  const previewParticipantId = context.dailyCall.remoteParticipants[0]?.video
    ? context.dailyCall.remoteParticipants[0].user_id
    : 'local';
  const previewName = context.dailyCall.remoteParticipants[0]?.user_name
    || context.dailyCall.localParticipant?.user_name
    || 'Videosprechstunde';
  const showVideoPreview = previewParticipantId === 'local'
    ? context.dailyCall.isCameraOn
    : Boolean(context.dailyCall.remoteParticipants[0]?.video);

  return (
    <div className="pointer-events-none fixed bottom-6 right-6 z-[70] flex w-[min(380px,calc(100vw-2rem))] justify-end">
      <section className="pointer-events-auto w-full overflow-hidden rounded-3xl border border-brand-200 bg-white/95 shadow-elevated backdrop-blur dark:border-brand-900/60 dark:bg-neutral-900/95">
        <div className="relative aspect-video w-full bg-neutral-950">
          {showVideoPreview ? (
            <DailyVideoTrack
              callFrame={context.dailyCall.callFrame}
              participantId={previewParticipantId}
              muted={previewParticipantId === 'local'}
              className="h-full w-full object-cover"
            />
          ) : (
            <div className="flex h-full w-full items-center justify-center bg-neutral-900 text-white/70">
              {previewParticipantId === 'local' ? <VideoOff className="h-8 w-8" /> : <PhoneCall className="h-8 w-8" />}
            </div>
          )}

          <div className="pointer-events-none absolute inset-x-0 top-0 flex items-center justify-between bg-gradient-to-b from-black/70 via-black/25 to-transparent px-3 py-2 text-white">
            <div className="flex min-w-0 items-center gap-2">
              <span className="h-2.5 w-2.5 shrink-0 rounded-full bg-emerald-400" />
              <p className="truncate text-xs font-semibold tracking-wide">
                {previewName}
              </p>
            </div>
            {!context.dailyCall.isMicOn && (
              <MicOff className="h-4 w-4 shrink-0 text-white/90" />
            )}
          </div>
        </div>

        <div className="flex items-start gap-3 p-4">
          <div className="mt-0.5 flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-brand-50 text-brand-600 dark:bg-brand-950/40 dark:text-brand-300">
            <PhoneCall className="h-5 w-5" />
          </div>

          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <p className="text-sm font-semibold text-neutral-900 dark:text-neutral-100">
                Videosprechstunde aktiv
              </p>
            </div>
            <p className="mt-1 text-sm text-neutral-600 dark:text-neutral-300">
              {statusLabel}
            </p>
          </div>
        </div>

        <div className="flex items-center justify-end px-4 pb-4">
          <button
            type="button"
            onClick={context.restoreSession}
            className={cn(
              'inline-flex min-h-[44px] items-center gap-2 rounded-xl bg-brand-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-brand-700',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2',
            )}
            aria-label="Zurück zum aktiven Gespräch"
          >
            <Maximize2 className="h-4 w-4" />
            Zurück zum Gespräch
          </button>
        </div>
      </section>
    </div>
  );
}
